import type {
  BrandedSearchCampaignBudget,
  BrandedSearchCampaignShare,
  BrandedSearchModel,
} from "~/shared/budget-recommender/branded-search"
import { emptySplitApi as api } from "./emptyApi"

export type BudgetRecommenderRowSettingDto = {
  id: string
  companyId: string
  entityType: "campaign" | "adset"
  entityId: string
  /** null = no saved period (a pin-only row); the page default applies. */
  analysisPeriodDays: number | null
  /** Held at its current budget while true and unexpired (APP 539). */
  pinned: boolean
  pinReason: string | null
  pinnedByUserId: string | null
  /** Email of whoever set the pin. */
  pinnedBy: string | null
  /** ISO timestamp. */
  pinnedAt: string | null
  /** ISO timestamp; null = until unpinned. */
  pinExpiresAt: string | null
  /** Per-row step cap override (fraction); null = the company's. */
  stepCapPct: number | null
}

/** Only the fields sent change (see the API's UpsertBudgetRecommenderRowSettingDto). */
export type UpsertBudgetRecommenderRowSettingDto = {
  entityType: "campaign" | "adset"
  entityId: string
  analysisPeriodDays?: number
  /** true pins (pinReason required), false unpins. */
  pinned?: boolean
  pinReason?: string | null
  /** ISO date-time; omit or null = until unpinned. */
  pinExpiresAt?: string | null
  /** 0.05–0.30; null clears the override. */
  stepCapPct?: number | null
}

/** An entity a human or agent changed inside the hold window — the recommender leaves it alone. */
/**
 * A recent manual change ("hold" is the API's historical name). The Budget
 * Recommender labels these rows rather than pinning them; see
 * `RecentChange` in the page's types.
 */
export type BudgetRecommenderHoldDto = {
  entityType: "campaign" | "adset" | "ad"
  entityId: string
  platform?: string
  reason: "budget" | "bid-target" | "enabled"
  /** ISO timestamp of the change. */
  at: string
  auditId: string
  auditType: string
  actor: string | null
  /** Budget changes only: daily budget before the change, in cents, when recorded. */
  previousDailyBudgetCents?: number
  /** Budget changes only: daily budget after the change, in cents, when recorded. */
  newDailyBudgetCents?: number
}

export type BudgetRecommenderHoldsDto = {
  /** The recent change window applied, in days (0 = labels off). */
  days: number
  since: string
  holds: BudgetRecommenderHoldDto[]
}

// ---- Budget recap (MD-5417): the client-shareable story of an approve ----

export type BudgetRecapCategory = "BRAND" | "DR"

export interface BudgetRecapTargets {
  targetDRRoas: number
  targetBrandRoas: number
  targetDailyBudget: number
}

export interface BudgetRecapMer {
  actualMer: number | null
  targetMer: number
  merLabel: string
}

export interface BudgetRecapTroas {
  fromTargetRoas: number
  toTargetRoas: number
}

/** One approved recommender row plus the outcome of its write. Mirrors `BudgetRecapChangeDto`. */
export interface BudgetRecapChange {
  entityId: string
  entityName: string
  entityType: "campaign" | "adset"
  campaignId: string
  channel: string
  initiativeType: string
  initiativeTypeLabel: string
  category: BudgetRecapCategory | null
  analysisPeriodDays: number
  /** YYYY-MM-DD */
  dataThroughDate: string
  periodSpend: number
  avgDailySpend: number
  drRoas: number
  brandRoas: number
  modeledRevenuePeriod: number
  brandValuePeriod: number
  currentDailyBudget: number
  /** Dollars; null for a pause. */
  newDailyBudget: number | null
  action: "update" | "pause"
  success: boolean
  confidence?: "LOW" | "MEDIUM" | "HIGH" | null
  troas?: BudgetRecapTroas
  /**
   * Set on a row deliberately left at its current budget (newDailyBudget =
   * currentDailyBudget): why it was kept, so the recap can justify it.
   */
  unchangedReason?: string
}

export interface BudgetRecapRequest {
  targets: BudgetRecapTargets
  mer?: BudgetRecapMer
  changes: BudgetRecapChange[]
  topAdsLimit?: number
}

export interface BudgetRecapAd {
  adId: string
  adName: string
  campaignId: string
  campaignName: string
  adsetName: string
  imageUrl: string | null
  adLink: string | null
  spend: number
  /** Modeled revenue (DR) or brand value (brand) over the window. */
  value: number
  roas: number
  purchases: number
}

export interface BudgetRecapResponse {
  /** Markdown. */
  recap: string
  generatedAt: string
  model: string
  scalingAds: BudgetRecapAd[]
  retractingAds: BudgetRecapAd[]
  adsUnavailable: boolean
}

/** GET /budget-recommender/branded-search — the shared model plus the per-campaign split. */
export interface BrandedSearchBudgetDto {
  /** n-1: the last complete data day every week ends on or before */
  dataThrough: string
  freshnessUnavailable: boolean
  weekStartDay: string
  fetchRange: [string, string]
  brandSearchPolicy: "fixed" | "optimize"
  /** The Financial Settings spend guardrail the default target divides by */
  guardrail: {
    type: string | null
    value: number | null
    scope: string | null
    display: string | null
  }
  /** Every BRANDED_SEARCH-typed Google campaign seen in the fetch, with trailing 4-week spend */
  campaigns: BrandedSearchCampaignShare[]
  /** The recommended weekly spend as per-campaign daily budgets; null without a recommendation */
  recommendedBudgets: BrandedSearchCampaignBudget[] | null
  model: Omit<BrandedSearchModel, "weeks"> & {
    weeks?: BrandedSearchModel["weeks"]
  }
}

export interface BrandedSearchBudgetQueryArg {
  /** Overrides the default target (value per session ÷ guardrail) */
  targetCostPerMarginalClick?: number
  includeWeeks?: boolean
}

const injectedRtkApi = api.injectEndpoints({
  endpoints: build => ({
    budgetRecommenderControllerGetBrandedSearch: build.query<
      BrandedSearchBudgetDto,
      BrandedSearchBudgetQueryArg | void
    >({
      query: arg => ({
        url: `/budget-recommender/branded-search`,
        params: {
          ...(arg && arg.targetCostPerMarginalClick !== undefined
            ? { targetCostPerMarginalClick: arg.targetCostPerMarginalClick }
            : {}),
          ...(arg && arg.includeWeeks ? { includeWeeks: "true" } : {}),
        },
      }),
      providesTags: ["BudgetRecommenderBrandedSearch"],
    }),
    budgetRecommenderControllerGetRowSettings: build.query<
      BudgetRecommenderRowSettingDto[],
      void
    >({
      query: () => ({ url: `/budget-recommender/row-settings` }),
      providesTags: ["BudgetRecommenderRowSettings"],
    }),
    budgetRecommenderControllerUpsertRowSetting: build.mutation<
      BudgetRecommenderRowSettingDto,
      UpsertBudgetRecommenderRowSettingDto
    >({
      query: queryArg => ({
        url: `/budget-recommender/row-settings`,
        method: "PUT",
        body: queryArg,
      }),
      invalidatesTags: ["BudgetRecommenderRowSettings"],
    }),
    budgetRecommenderControllerGetHolds: build.query<
      BudgetRecommenderHoldsDto,
      { days?: number } | void
    >({
      query: arg => ({
        url: `/budget-recommender/holds`,
        ...(arg && arg.days !== undefined
          ? { params: { days: arg.days } }
          : {}),
      }),
      providesTags: ["BudgetRecommenderHolds"],
    }),
    budgetRecommenderControllerGenerateRecap: build.mutation<
      BudgetRecapResponse,
      BudgetRecapRequest
    >({
      query: body => ({
        url: `/budget-recommender/recap`,
        method: "POST",
        body,
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useBudgetRecommenderControllerGetBrandedSearchQuery,
  useBudgetRecommenderControllerGetRowSettingsQuery,
  useBudgetRecommenderControllerUpsertRowSettingMutation,
  useBudgetRecommenderControllerGetHoldsQuery,
  useBudgetRecommenderControllerGenerateRecapMutation,
} = injectedRtkApi
